import BrandPromoPoster from "../../../static/media/home/brand-promo-video-poster.jpg";
import BrandPromoVideo from "../../../static/media/home/brand-promo-video.mp4";
import HomePageCarouselVideo from "../../../static/media/home/homepage_carousel_video.mp4";
import HomePageCarouselVideoMobile from "../../../static/media/home/homepage_carousel_video_mobile.mp4";
import VideoMobileMp4 from "../../../static/media/home/main-video-mobile.mp4";
import VideoMobileWebm from "../../../static/media/home/main-video-mobile.webm";
import PosterMobile from "../../../static/media/home/main-video-poster-mobile.webp";
import Poster from "../../../static/media/home/main-video-poster.webp";
import VideoMp4 from "../../../static/media/home/main-video.mp4";
import VideoWebm from "../../../static/media/home/main-video.webm";
import { useIsMobile } from "../../hooks/useIsMobile";
import { MainVideo } from "../Sections/MainVideo";
import * as React from "react";

const ASPECT_RATIO = 16 / 9;

function useVideoSize() {
  const [videoWidth, setVideoWidth] = React.useState<number>(0);

  function calculateVideoWidth(): number {
    const windowWidth = window.innerWidth;

    let width = windowWidth * 0.59;

    if (windowWidth < 900) {
      width = windowWidth * 0.9;
    }

    if (windowWidth > 901 && windowWidth < 1600) {
      width = windowWidth * 0.67;
    }
    if (windowWidth > 1600) {
      width = 1048;
    }

    return Math.floor(width);
  }

  React.useEffect(() => {
    const updateSize = () => setVideoWidth(calculateVideoWidth());
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  return videoWidth;
}

interface VideoContainerProps {
  readonly children: React.ReactNode;
  readonly videoWidth: number;
  readonly className?: string;
}

function VideoContainer({ children, videoWidth, className = "" }: VideoContainerProps) {
  return (
    <div className="tw-relative tw-flex tw-flex-col tw-items-center">
      <div
        style={{
          minWidth: `${videoWidth}px`,
        }}
        className={`safari-ios-overflow-fix !tw-overflow-hidden !tw-rounded-lg ${className}`}
      >
        <div
          style={{
            minWidth: `${videoWidth}px`,
            height: `${videoWidth / ASPECT_RATIO}px`,
          }}
          className="tw-relative tw-bg-[#010101]"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function OriginalVideo() {
  return (
    <div className="tw-pb-[50px] tw-pt-[60px]">
      <MainVideo
        posterMobile={PosterMobile}
        poster={Poster}
        videoMobileMp4={VideoMobileMp4}
        videoMp4={VideoMp4}
        videoMobileWebm={VideoMobileWebm}
        videoWebm={VideoWebm}
      />
    </div>
  );
}

export function VideoWithPlayButton() {
  const videoWidth = useVideoSize();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isVideoReady, setIsVideoReady] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const focusStyles =
    "focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-0 focus-visible:tw-outline-brand_scale-500";

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      handlePlayPause();
    }
  };

  return (
    <VideoContainer videoWidth={videoWidth} className="tw-relative">
      <video
        ref={videoRef}
        loop={true}
        muted={false}
        playsInline={true}
        preload="metadata"
        poster={BrandPromoPoster}
        width={videoWidth}
        height={videoWidth / ASPECT_RATIO}
        onCanPlayThrough={() => setIsVideoReady(true)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      >
        <source src={BrandPromoVideo} type="video/mp4" />
        <track label="English" kind="captions" srcLang="en" src="" />
      </video>

      {isVideoReady && (
        <div
          role="button"
          onClick={handlePlayPause}
          className={`tw-aspect-16/9 tw-absolute tw-inset-0 tw-flex tw-w-full tw-items-center tw-justify-center tw-transition-all focus-within:tw-opacity-100 ${!isPlaying ? "tw-opacity-1" : "tw-opacity-0 hover:tw-opacity-100"} ${
            isPlaying
              ? "tw-bg-transparent hover:tw-bg-black hover:tw-bg-opacity-20"
              : "tw-bg-black tw-bg-opacity-30 hover:tw-bg-opacity-40"
          }`}
        >
          <button
            onClick={handlePlayPause}
            onKeyDown={handleKeyDown}
            aria-label={isPlaying ? "Pause video" : "Play video"}
            className={`tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-shadow-lg tw-transition-all ${focusStyles} ${
              isPlaying ? "tw-bg-black tw-bg-opacity-70 tw-text-white " : "tw-bg-white tw-text-black"
            }`}
          >
            {isPlaying ? (
              <svg className="tw-h-8 tw-w-8 tw-text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M5 4.5A1.5 1.5 0 016.5 3h1A1.5 1.5 0 019 4.5v11A1.5 1.5 0 017.5 17h-1A1.5 1.5 0 015 15.5v-11zM11 4.5A1.5 1.5 0 0112.5 3h1A1.5 1.5 0 0115 4.5v11a1.5 1.5 0 01-1.5 1.5h-1A1.5 1.5 0 0111 15.5v-11z"
                />
              </svg>
            ) : (
              <svg className="tw-ml-1 tw-h-8 tw-w-8" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            )}
          </button>
        </div>
      )}
    </VideoContainer>
  );
}

export function VideoAutoplay() {
  const videoWidth = useVideoSize();

  return (
    <VideoContainer videoWidth={videoWidth}>
      <video
        autoPlay={true}
        loop={true}
        muted={true}
        playsInline={true}
        preload="auto"
        poster={BrandPromoPoster}
        width={videoWidth}
        height={videoWidth / ASPECT_RATIO}
      >
        <source src={BrandPromoVideo} type="video/mp4" />
        <track label="English" kind="captions" srcLang="en" src="" />
      </video>
    </VideoContainer>
  );
}

export function CarouselVideo() {
  const isMobile = useIsMobile(620);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) {
      return;
    }

    videoEl.pause();
    videoEl.currentTime = 0;
    videoEl.load();
    const playPromise = videoEl.play();
    playPromise?.catch(() => {
      /* autoplay may be blocked; ignore */
    });
  }, [isMobile]);

  return (
    <div className="tw-relative tw-w-full">
      <div className="safari-ios-overflow-fix tw-w-full !tw-overflow-hidden !tw-rounded-lg">
        <div className="tw-aspect-16/9 tw-relative tw-w-full tw-bg-[#010101]">
          <video
            ref={videoRef}
            key={isMobile ? "mobile" : "desktop"}
            autoPlay={true}
            loop={true}
            muted={true}
            playsInline={true}
            preload="auto"
            className="tw-h-full tw-w-full tw-object-cover"
          >
            <source src={isMobile ? HomePageCarouselVideoMobile : HomePageCarouselVideo} type="video/mp4" />
            <track label="English" kind="captions" srcLang="en" src="" />
          </video>
        </div>
      </div>
    </div>
  );
}
